import {
  BundleOrderItem,
  BundleProduct,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';

import {
  getOptionalItems,
  getRequiredItems,
} from '@storefront/app/pdp-config/printer/order-details/order-details.utils';

import {
  BundleItemOptionWithLowestPrice,
  BundleItemWithLowestPrice,
  BundleProductWithLowestPrice,
} from '@storefront/app/pdp-config/printer/product-configuration/product-configuration.utils';

import { AttentiveProductItemType, ProductView } from './AttentiveTypes';
import {
  ProductConfiguration,
  ProductOptions,
} from '@storefront/types/Product';

export const bundleItemToProductView = (
  bundleItemWithLowestPrice: BundleItemWithLowestPrice,
  selectedOption: BundleItemOptionWithLowestPrice
) => ({
  productId: bundleItemWithLowestPrice.uid,
  productVariantId: selectedOption.uid,
  name: selectedOption.label,
  productImage: selectedOption.product.image.url,
  category: selectedOption.product.hp_product_type_label,
  price: {
    value:
      selectedOption?.product.price_range.minimum_price.regular_price.value.toString() ||
      '',
    currency:
      selectedOption?.product.price_range.minimum_price.regular_price
        .currency || 'USD',
  },
  quantity: selectedOption.quantity,
});

export const getItemsSelected = (
  items: BundleItemWithLowestPrice[],
  selectedOptions: string[]
) =>
  items
    .map((item) => {
      const selectedOption = item.options.find((option) =>
        selectedOptions.includes(option.uid)
      );
      return selectedOption
        ? bundleItemToProductView(item, selectedOption)
        : null;
    })
    .filter((item) => item !== null);

export const mergeRequiredAndOptional = (
  requiredItemsSelected: ProductView['items'],
  optionalItemsSelected?: ProductView['items']
) => {
  const filteredOptionalItemsSelected = optionalItemsSelected.filter(
    (item) => item !== undefined
  );

  const itemsSelected =
    filteredOptionalItemsSelected?.length > 0
      ? [...requiredItemsSelected, ...filteredOptionalItemsSelected]
      : [...requiredItemsSelected];
  return itemsSelected;
};

export const getProductViewItems = (
  selectedProductItems: BundleItemWithLowestPrice[],
  selectedOptions: string[]
) => {
  const filteredHiddenItems = selectedProductItems.filter(
    (item) => item.hidden === 0
  );
  const requiredItems = getRequiredItems(filteredHiddenItems);
  const requiredItemsSelected = getItemsSelected(
    requiredItems,
    selectedOptions
  );

  const optionalItems = getOptionalItems(filteredHiddenItems);
  const optionalItemsSelected = getItemsSelected(
    optionalItems,
    selectedOptions
  );

  const itemsSelected = mergeRequiredAndOptional(
    requiredItemsSelected,
    optionalItemsSelected
  );
  return itemsSelected;
};

export const getSelectedProductItems = (
  activeSKU: string,
  products: GetSkuProductsQuery['products']
) =>
  activeSKU
    ? (
        products?.items?.find(
          (product) => product.sku === activeSKU
        ) as BundleProductWithLowestPrice
      )?.items
    : [];

export const bundleOrderItemToFormattedProductAttentive = (
  item: BundleOrderItem
) =>
  item.bundle_options?.flatMap((bundleOption) =>
    bundleOption.values.map((value) => ({
      productId: bundleOption.uid,
      productVariantId: value.product_sku,
      name:
        value.hp_product_type_label === 'instantInk'
          ? `${value.product_name} - Instant Ink`
          : value.product_name,
      price: {
        value: value.price?.value,
        currency: value.price?.currency || 'USD',
      },
      quantity: value.quantity,
      category: value.hp_product_type_label,
    }))
  );

export const convertCartInformation = (
  configuration: ProductConfiguration,
  product: BundleProduct
): AttentiveProductItemType[] => {
  const activeSKU = configuration.activeSku as string;
  const configs = configuration[activeSKU] as ProductOptions;

  const selectedItems: any[] = Object.entries(configs).map(([key, value]) => ({
    key,
    value,
  }));

  selectedItems.unshift({
    key: product.items[0].uid,
    value: product.items[0].options[0].uid,
  });

  return selectedItems
    .map((selectedItem) => {
      const productItem = product.items.find(
        (item) => item.uid === selectedItem.key
      );
      if (!productItem) return undefined;

      const productOption = productItem.options.find(
        (option) => option.uid === selectedItem.value
      );
      if (!productOption) return undefined;

      return {
        productId: productItem.uid,
        productVariantId: productOption.uid,
        name: productOption.label,
        productImage: productOption.product?.image?.url || '',
        category: productOption.product?.hp_product_type_label,
        price: {
          value:
            productOption?.product?.price_range?.minimum_price?.regular_price?.value.toString() ||
            '',
          currency:
            productOption?.product?.price_range?.minimum_price?.regular_price
              ?.currency || 'USD',
        },
        quantity: productOption?.quantity || 1,
      };
    })
    .filter((item) => item !== undefined);
};
